import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { WebRoutes } from '@shared/constants/web-routes.enum';
import { resolveLandingRoute } from '@features/shared/auth/domain/utils/landing-route.util';
import { AuthStore } from '@features/shared/auth/presentation/store/auth.store';
import { ClinicSelectionSessionService } from '../services/clinic-selection-session.service';

// A deliberate revisit -- the `uv-user-menu` clinic switcher and the "switched
// elsewhere" session-conflict flow both send an already-resolved multi-clinic account back here on
// purpose, so `needsClinicSelection` alone (false once a clinic is the active session) must not
// bounce them straight back out.
const DELIBERATE_REVISIT_REASONS = new Set(['switch', 'switched-elsewhere']);

function needsClinicSelection(route: ActivatedRouteSnapshot): boolean {
  const store = inject(AuthStore);
  const staffClinicsCount = store.user()?.staffClinics?.length ?? 0;
  const reason = route.queryParamMap.get('reason');
  return store.user()?.accountRealm === 'administration' && (staffClinicsCount > 1 || (reason !== null && DELIBERATE_REVISIT_REASONS.has(reason)));
}

// Guards `/elige-clinica` itself: only a staff account that actually has more than one clinic
// membership, or a deliberate revisit (see above), may enter. Anyone else is bounced to their
// normal landing route instead -- same shape as `onboardingGuard`.
export const clinicSelectorGuard: CanActivateFn = (route) => {
  if (needsClinicSelection(route)) return true;

  const store = inject(AuthStore);
  const role = store.user()?.role ?? null;
  const hasCompletedOnboarding = store.user()?.hasCompletedOnboarding ?? true;
  return inject(Router).createUrlTree([resolveLandingRoute(role, hasCompletedOnboarding, false, store.user()?.accountRealm)]);
};

// Guards the admin shell (and every child route under it): a staff account with more than one
// clinic membership must not be able to bypass the selector by deep-linking `/dashboard` or any of
// its children. Once the switch endpoint has minted a clinic-scoped session, the explicit selection
// marker lets that exact user/organization pair enter instead of redirecting it back into the
// selector forever.
export const clinicSelectionRequiredGuard: CanActivateFn = () => {
  const store = inject(AuthStore);
  const selectionSession = inject(ClinicSelectionSessionService);
  const user = store.user();
  const staffClinicsCount = store.user()?.staffClinics?.length ?? 0;

  if (staffClinicsCount > 1 && !selectionSession.hasSelectionFor(user)) {
    return inject(Router).createUrlTree([WebRoutes.ClinicSelector]);
  }

  return true;
};
